// frontend/src/components/staff/StaffPromoManagementPage.js
//
// PROMO-RF-E — Staff Promo Code Management: LIST + DETAIL view (FE).
// Route: /staff/promo-management  (mounted in App.js — DISTINCT from the
// LEGACY in-house /staff/promo-codes surface, which ADR-029 D4 deletes LAST
// via PROMO-RF-H; this story does NOT touch that route/component).
//
// Story:    docs/backlog/PROMO-RF-E-staff-promo-management-list-view.md
// ADR:      docs/architecture/ADR-029-promo-reframe-reconcile.md
//             D2 — customer discount math STAYS on Stripe-hosted Checkout;
//                  this surface is staff management METADATA ONLY (no in-app
//                  customer discount calculation, no booking-time live
//                  lookup). Cited; NOT re-opened.
//             D5 — RF-E consumes the RF-B contract VERBATIM (←RF-B / PR #181).
//
// Scope (RF-E — LIST/DETAIL ONLY):
//   - List table reading GET /api/staff/promo-codes (Stripe-sourced, Q1=A)
//     — code string, discount summary, active/archived status pill, usage
//     (timesRedeemed / maxRedemptions), expiry. Active-only / All filter.
//   - Row click opens a right-side detail drawer reading
//     GET /api/staff/promo-codes/:id (Stripe-sourced detail).
//   - Empty / loading / error (retryable) states (AC Scenario 4).
//   - Staff-route-gated at App.js (AC Scenario 5; BE re-checks via
//     authorizeRoles("staff","admin")).
//   - B1 lock (AC Scenario 3): NO in-house redemption-history panel and NO
//     UI affordance implying preserved legacy in-house history. Only the
//     Stripe-sourced `timesRedeemed` usage counter is shown.
//
// RF-F seam — FILLED by PROMO-RF-F (←RF-B / #181), same page surface,
//   pure-additive on top of RF-E:
//   - header `page-actions-slot` now hosts a "New promo code" button that
//     opens PromoCreateModal; on create success the list is optimistically
//     updated + refreshed and a success banner is shown (AC Sc.1/2/4/5).
//   - drawer `drawer-footer-actions-slot` now hosts a per-code "Archive"
//     action (active codes only) that opens PromoArchiveConfirm; on confirm
//     the RF-B archive endpoint is called (Stripe active:false — a
//     DEACTIVATE, never a destructive delete) and the list/drawer reflect
//     the archived state (AC Sc.3).
//   RF-E's list/detail behavior is UNCHANGED — RF-F only fills the two
//   reserved seams + adds a success banner; no RF-E flow is regressed.
//   NO in-house authoritative write, NO legacy redemption-history
//   affordance (MSG-201 Q1=A + B1; AC Sc.5). The legacy in-house
//   /api/promo-codes engine is RF-H's removal — RF-F does NOT touch it
//   (ADR-029 D4).
//
// State management: codebase has NO TanStack Query (verified in
//   BroadcastHistoryPage / AdminStaffAvailabilityDashboard). Plain useState
//   + a single `fetchList` keyed on the active filter, re-fired on
//   refresh / filter-change / retry — same pattern as BroadcastHistoryPage.
//
// Production-safety: pure-additive new route + new component + additive
//   service/config entries + additive sidebar link. ZERO edits to the
//   legacy /staff/promo-codes route, the legacy PromoCodeManagement
//   component, or any existing booking/auth/payment flow. Single-commit
//   revert = clean rollback. NOT a Customer-Facing Gate (R-020) — staff
//   management surface, synchronous reads, no async vendor signal drives
//   customer-visible state (ADR-029: "NOT a customer-facing gate").
//
// RF-B response shape consumed VERBATIM (PR #181 shapePromotionCode):
//   list    -> { success: true, data: [shaped], hasMore }
//   getById -> { success: true, data: shaped }
//   shaped  -> { id, code, active, status: "active"|"archived",
//               timesRedeemed, maxRedemptions, expiresAt, created,
//               restrictions, metadata, coupon: { id, name, percentOff,
//               amountOff, currency, duration, valid } | null }
//   error   -> { success:false, error, reason }  (HTTP 503 = retryable
//               vendor_unavailable per ADR-029 D3)

import React from "react";
import {
  FaTag,
  FaSync,
  FaExclamationTriangle,
  FaTimes,
  FaPlus,
  FaCheckCircle,
} from "react-icons/fa";
import moment from "moment-timezone";
import { useAuth } from "../AuthContext";
import { staffPromoAPI } from "../../services/api";
import PromoCreateModal from "./PromoCreateModal";
import PromoArchiveConfirm from "./PromoArchiveConfirm";
import "./StaffPromoManagementPage.css";

const STUDIO_TZ = "America/Phoenix";
const STUDIO_TZ_LABEL = "MST";

// BE caps at 100; request the cap so the list is single-page for the
// expected promo-code volume (small set).
const LIST_LIMIT = 100;

// Filter options for the active/archived toggle (AC Scenario 2 surfaces
// both; "All" is the default so archived codes are visible alongside
// active ones without an extra click).
const FILTERS = Object.freeze({
  ALL: "all",
  ACTIVE: "active",
  ARCHIVED: "archived",
});

// ----- Pure helpers (testable) ------------------------------------------

/**
 * Format a Stripe unix-seconds timestamp in America/Phoenix with explicit
 * "MST" label. Returns "" for null/undefined/invalid so callers render the
 * "—" fallback rather than "Invalid date".
 * @param {number|null} unixSeconds
 */
export function formatPhoenixUnix(unixSeconds) {
  if (unixSeconds == null || unixSeconds === "") return "";
  const m = moment.unix(Number(unixSeconds)).tz(STUDIO_TZ);
  if (!m.isValid()) return "";
  return `${m.format("MMM D, YYYY, h:mm A")} ${STUDIO_TZ_LABEL}`;
}

/**
 * Human-readable discount summary from the RF-B-shaped `coupon` block.
 * percentOff -> "20% off"; amountOff (cents) -> "$15.00 off"; neither -> "—".
 * Stripe is the source of truth for the discount value (Q1 = Option A).
 * @param {object|null} coupon - RF-B shaped coupon
 */
export function discountSummary(coupon) {
  if (!coupon) return "—";
  if (coupon.percentOff != null) {
    return `${coupon.percentOff}% off`;
  }
  if (coupon.amountOff != null) {
    const dollars = (Number(coupon.amountOff) / 100).toFixed(2);
    const cur = (coupon.currency || "usd").toUpperCase();
    const sym = cur === "USD" ? "$" : "";
    return `${sym}${dollars}${sym ? "" : ` ${cur}`} off`;
  }
  return "—";
}

/**
 * Usage summary from the Stripe-sourced counters. "3 / 100", "3 / ∞" when
 * unlimited, "—" when redemption count is absent. This is the ONLY usage
 * surface — there is intentionally NO in-house redemption-history view
 * (MSG-201 B1 lock; AC Scenario 3).
 * @param {object} row - RF-B shaped row
 */
export function usageSummary(row) {
  if (!row || row.timesRedeemed == null) return "—";
  const used = row.timesRedeemed;
  const cap = row.maxRedemptions;
  return `${used} / ${cap != null ? cap : "∞"}`;
}

/**
 * Status-pill descriptor: { label, tone } where tone is "active" or
 * "archived". Drives the pill background tint (AC Scenario 2 — archived
 * visually distinct from active). Falls back to archived for an
 * unexpected/missing status so a code is never mis-shown as active.
 * @param {object} row - RF-B shaped row
 */
export function statusPillFor(row) {
  if (row && (row.status === "active" || row.active === true)) {
    return { label: "Active", tone: "active" };
  }
  return { label: "Archived", tone: "archived" };
}

/**
 * Min-purchase restriction note from the RF-B-shaped `restrictions` block,
 * for the detail drawer. Returns "" when no minimum is set.
 * @param {object|null} restrictions
 */
export function restrictionNote(restrictions) {
  if (!restrictions) return "";
  const min = restrictions.minimum_amount;
  if (min == null) return "";
  const dollars = (Number(min) / 100).toFixed(2);
  const cur = (restrictions.minimum_amount_currency || "usd").toUpperCase();
  const sym = cur === "USD" ? "$" : "";
  return `Minimum purchase ${sym}${dollars}${sym ? "" : ` ${cur}`}`;
}

/**
 * Translate an axios error from the RF-B endpoints into a user-facing
 * message. Honors the ADR-029 D3 unified shape: a 503 / vendor_unavailable
 * is the retryable "Stripe temporarily unavailable" message; everything
 * else surfaces the BE-provided `error` string (never a raw object).
 * @param {Error} err - axios error
 */
export function messageForError(err) {
  const data = err?.response?.data;
  const reason = data?.reason;
  if (reason === "vendor_unavailable" || err?.response?.status === 503) {
    return "Stripe is temporarily unavailable. Please try again in a moment.";
  }
  return (
    data?.error ||
    err?.userMessage ||
    "Couldn't load promo codes. Please try again."
  );
}

// ----- Detail drawer subcomponent ---------------------------------------
// Reads GET /api/staff/promo-codes/:id (RF-B retrieve). Stripe-sourced
// detail only. NO in-house redemption-history panel (B1 / AC Scenario 3).

const PromoDetailDrawer = ({
  promotionCodeId,
  onClose,
  // RF-F seam — when provided, the drawer renders an "Archive" action in
  // its reserved footer slot for ACTIVE codes only. RF-E passed neither
  // (LIST/DETAIL ONLY); RF-F threads these through the page component.
  onArchiveClick,
  archivedId,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [detail, setDetail] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    staffPromoAPI
      .getById(promotionCodeId)
      .then((res) => {
        if (cancelled) return;
        setDetail(res?.data?.data || null);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(messageForError(err));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [promotionCodeId]);

  // Close on Escape (drawer parity with BroadcastHistoryPage).
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pill = detail ? statusPillFor(detail) : null;
  const restriction = detail ? restrictionNote(detail.restrictions) : "";

  return (
    <div
      className="promo-mgmt-drawer-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="promo-mgmt-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Promo code detail"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="promo-mgmt-drawer-header">
          <h2>Promo code detail</h2>
          <button
            type="button"
            className="promo-mgmt-drawer-close"
            aria-label="Close detail"
            onClick={onClose}
          >
            <FaTimes aria-hidden="true" />
          </button>
        </header>

        <div className="promo-mgmt-drawer-body">
          {loading && (
            <div
              className="promo-mgmt-drawer-loading"
              role="status"
              aria-live="polite"
            >
              Loading promo code detail…
            </div>
          )}

          {error && !loading && (
            <div className="promo-mgmt-drawer-error" role="alert">
              <FaExclamationTriangle aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {detail && !loading && !error && (
            <>
              <section className="promo-mgmt-drawer-section">
                <div className="promo-mgmt-drawer-label">Code</div>
                <div className="promo-mgmt-drawer-value promo-mgmt-drawer-code">
                  {detail.code || "—"}
                </div>
              </section>

              <section className="promo-mgmt-drawer-section">
                <div className="promo-mgmt-drawer-label">Status</div>
                <div className="promo-mgmt-drawer-value">
                  <span
                    className={`promo-mgmt-pill promo-mgmt-pill--${pill.tone}`}
                    aria-label={`Status: ${pill.label}`}
                  >
                    {pill.label}
                  </span>
                </div>
              </section>

              <section className="promo-mgmt-drawer-section">
                <div className="promo-mgmt-drawer-label">Discount</div>
                <div className="promo-mgmt-drawer-value">
                  {discountSummary(detail.coupon)}
                </div>
              </section>

              <section className="promo-mgmt-drawer-section">
                <div className="promo-mgmt-drawer-label">
                  Redemptions (from Stripe)
                </div>
                <div
                  className="promo-mgmt-drawer-value"
                  data-testid="drawer-usage"
                >
                  {usageSummary(detail)}
                </div>
              </section>

              <section className="promo-mgmt-drawer-section">
                <div className="promo-mgmt-drawer-label">Expires</div>
                <div className="promo-mgmt-drawer-value">
                  {formatPhoenixUnix(detail.expiresAt) || "No expiry"}
                </div>
              </section>

              <section className="promo-mgmt-drawer-section">
                <div className="promo-mgmt-drawer-label">Created</div>
                <div className="promo-mgmt-drawer-value">
                  {formatPhoenixUnix(detail.created) || "—"}
                </div>
              </section>

              {restriction && (
                <section className="promo-mgmt-drawer-section">
                  <div className="promo-mgmt-drawer-label">Restrictions</div>
                  <div className="promo-mgmt-drawer-value">{restriction}</div>
                </section>
              )}

              {detail.coupon && (
                <section className="promo-mgmt-drawer-section">
                  <div className="promo-mgmt-drawer-label">Stripe coupon</div>
                  <div className="promo-mgmt-drawer-value promo-mgmt-drawer-ids">
                    <div>
                      <span className="promo-mgmt-drawer-id-key">
                        Coupon ID:
                      </span>{" "}
                      {detail.coupon.id || "—"}
                    </div>
                    <div>
                      <span className="promo-mgmt-drawer-id-key">
                        Promotion code ID:
                      </span>{" "}
                      {detail.id || "—"}
                    </div>
                  </div>
                </section>
              )}

              {/* RF-F seam FILLED — per-code archive action. Shown only
                  for ACTIVE codes; archive is a Stripe deactivate
                  (active:false), NOT a destructive delete, and is
                  reversible-by-recreate only (AC Sc.3). Once archived
                  (optimistically reflected via `archivedId`) the action
                  is replaced by a non-interactive note. */}
              <div
                className="promo-mgmt-drawer-footer"
                data-testid="drawer-footer-actions-slot"
              >
                {typeof onArchiveClick === "function" &&
                detail.status === "active" &&
                detail.active === true &&
                archivedId !== detail.id ? (
                  <button
                    type="button"
                    className="promo-mgmt-drawer-archive-btn"
                    onClick={() => onArchiveClick(detail)}
                    data-testid="drawer-archive-btn"
                  >
                    Archive code
                  </button>
                ) : (
                  (detail.status === "archived" ||
                    archivedId === detail.id) && (
                    <p
                      className="promo-mgmt-drawer-archived-note"
                      data-testid="drawer-archived-note"
                    >
                      Archived in Stripe — reversible only by recreating a
                      code with the same definition.
                    </p>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

// ----- Page component ---------------------------------------------------

const StaffPromoManagementPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [filter, setFilter] = React.useState(FILTERS.ALL);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [openDetailId, setOpenDetailId] = React.useState(null);

  // RF-F — create modal + archive confirm + success banner + the
  // optimistically-archived id so the open drawer reflects archive
  // immediately (before the background list re-fetch resolves).
  const [createOpen, setCreateOpen] = React.useState(false);
  const [archiveTarget, setArchiveTarget] = React.useState(null);
  const [archiving, setArchiving] = React.useState(false);
  const [archiveError, setArchiveError] = React.useState(null);
  const [archivedId, setArchivedId] = React.useState(null);
  const [successMsg, setSuccessMsg] = React.useState(null);

  const fetchList = React.useCallback(
    async ({ activeFilter = FILTERS.ALL, mode = "initial" } = {}) => {
      if (mode === "initial") setLoading(true);
      if (mode === "refresh") setRefreshing(true);
      setError(null);
      try {
        const params = { limit: LIST_LIMIT };
        if (activeFilter === FILTERS.ACTIVE) params.active = true;
        if (activeFilter === FILTERS.ARCHIVED) params.active = false;
        const res = await staffPromoAPI.list(params);
        setRows(res?.data?.data || []);
      } catch (err) {
        setError(messageForError(err));
        setRows([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  React.useEffect(() => {
    fetchList({ activeFilter: filter, mode: "initial" });
  }, [fetchList, filter]);

  const handleRefresh = () =>
    fetchList({ activeFilter: filter, mode: "refresh" });
  const handleRetry = () =>
    fetchList({ activeFilter: filter, mode: "initial" });

  // RF-F AC Sc.1 — create succeeded in the modal (Stripe pass-through
  // already returned the shaped row). Optimistically prepend the new row
  // so it is visible without waiting, then re-fetch from Stripe (the
  // source of truth, Q1=A) so usage/derived fields reconcile. Show a
  // success confirmation. NO in-house write occurs anywhere (Sc.5).
  const handleCreated = (created) => {
    setCreateOpen(false);
    if (created && created.id) {
      setRows((prev) => {
        if (prev.some((r) => r.id === created.id)) return prev;
        return [created, ...prev];
      });
      setSuccessMsg(
        `Promo code ${created.code || "created"} created in Stripe.`
      );
    } else {
      setSuccessMsg("Promo code created in Stripe.");
    }
    fetchList({ activeFilter: filter, mode: "refresh" });
  };

  // RF-F AC Sc.3 — archive confirmed. Calls the RF-B archive endpoint
  // (Stripe active:false — a DEACTIVATE, never a destructive delete). On
  // success: optimistically flip the row to archived in place, mark
  // `archivedId` so the open drawer reflects it, then re-fetch from
  // Stripe so the list is reconciled with the source of truth.
  const handleArchiveConfirm = async () => {
    if (!archiveTarget || archiving) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      const res = await staffPromoAPI.archive(archiveTarget.id);
      const updated = res?.data?.data || null;
      setRows((prev) =>
        prev.map((r) =>
          r.id === archiveTarget.id
            ? {
                ...r,
                ...(updated || {}),
                active: false,
                status: "archived",
              }
            : r
        )
      );
      setArchivedId(archiveTarget.id);
      setSuccessMsg(
        `Promo code ${
          archiveTarget.code || ""
        } archived (deactivated in Stripe). It is reversible only by recreating a code with the same definition.`
      );
      setArchiveTarget(null);
      setArchiving(false);
      fetchList({ activeFilter: filter, mode: "refresh" });
    } catch (err) {
      setArchiveError(messageForError(err));
      setArchiving(false);
    }
  };

  return (
    <div className="promo-mgmt-page">
      {isAdmin && (
        <div
          className="promo-mgmt-admin-mode-bar"
          role="status"
          aria-label="Admin mode"
        />
      )}

      <header className="promo-mgmt-page-header">
        <h1>Promo Code Management</h1>
        <p className="promo-mgmt-page-subtitle">
          Stripe is the source of truth — codes are managed here, customer
          discounts apply at Stripe checkout
        </p>
        {/* RF-F seam FILLED — the "New promo code" button now lives in
            this slot alongside the RF-E filter + Refresh controls. */}
        <div
          className="promo-mgmt-page-actions"
          data-testid="page-actions-slot"
        >
          <div
            className="promo-mgmt-filter"
            role="group"
            aria-label="Filter promo codes by status"
          >
            <button
              type="button"
              className={`promo-mgmt-filter-btn${
                filter === FILTERS.ALL ? " promo-mgmt-filter-btn--on" : ""
              }`}
              aria-pressed={filter === FILTERS.ALL}
              onClick={() => setFilter(FILTERS.ALL)}
            >
              All
            </button>
            <button
              type="button"
              className={`promo-mgmt-filter-btn${
                filter === FILTERS.ACTIVE ? " promo-mgmt-filter-btn--on" : ""
              }`}
              aria-pressed={filter === FILTERS.ACTIVE}
              onClick={() => setFilter(FILTERS.ACTIVE)}
            >
              Active
            </button>
            <button
              type="button"
              className={`promo-mgmt-filter-btn${
                filter === FILTERS.ARCHIVED
                  ? " promo-mgmt-filter-btn--on"
                  : ""
              }`}
              aria-pressed={filter === FILTERS.ARCHIVED}
              onClick={() => setFilter(FILTERS.ARCHIVED)}
            >
              Archived
            </button>
          </div>
          <button
            type="button"
            className="promo-mgmt-btn-outline"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            aria-label="Refresh promo codes"
          >
            <FaSync
              aria-hidden="true"
              className={refreshing ? "promo-mgmt-spin" : ""}
            />{" "}
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className="promo-mgmt-btn-primary promo-mgmt-btn-new"
            onClick={() => {
              setSuccessMsg(null);
              setCreateOpen(true);
            }}
            data-testid="promo-new-btn"
            aria-label="New promo code"
          >
            <FaPlus aria-hidden="true" /> New promo code
          </button>
        </div>
      </header>

      {successMsg && (
        <div
          className="promo-mgmt-success"
          role="status"
          aria-live="polite"
          data-testid="promo-mgmt-success"
        >
          <FaCheckCircle aria-hidden="true" />
          <span>{successMsg}</span>
          <button
            type="button"
            className="promo-mgmt-success-dismiss"
            onClick={() => setSuccessMsg(null)}
            aria-label="Dismiss confirmation"
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>
      )}

      <main className="promo-mgmt-page-body">
        {loading && (
          <div
            className="promo-mgmt-skeleton"
            role="status"
            aria-live="polite"
            aria-label="Loading promo codes"
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="promo-mgmt-skeleton-row"
                aria-hidden="true"
              />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="promo-mgmt-error" role="alert">
            <FaExclamationTriangle
              className="promo-mgmt-error-icon"
              aria-hidden="true"
            />
            <div className="promo-mgmt-error-title">
              Couldn't load promo codes
            </div>
            <div className="promo-mgmt-error-body">{error}</div>
            <button
              type="button"
              className="promo-mgmt-btn-primary"
              onClick={handleRetry}
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="promo-mgmt-empty" role="status">
            <FaTag className="promo-mgmt-empty-icon" aria-hidden="true" />
            <div className="promo-mgmt-empty-title">
              {filter === FILTERS.ARCHIVED
                ? "No archived promo codes"
                : filter === FILTERS.ACTIVE
                ? "No active promo codes"
                : "No promo codes yet"}
            </div>
            <div className="promo-mgmt-empty-body">
              Promo codes are managed in Stripe. None match this view.
            </div>
          </div>
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <div className="promo-mgmt-table-wrap">
              <table
                className="promo-mgmt-table"
                aria-label="Promo codes"
              >
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Discount</th>
                    <th scope="col" className="promo-mgmt-th-num">
                      Redemptions
                    </th>
                    <th scope="col">Expires</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const pill = statusPillFor(row);
                    return (
                      <tr
                        key={row.id}
                        className={`promo-mgmt-row promo-mgmt-row--${pill.tone}`}
                        onClick={() => setOpenDetailId(row.id)}
                        tabIndex={0}
                        role="button"
                        aria-label={`Open detail for promo code ${
                          row.code || "unknown"
                        }: ${discountSummary(row.coupon)}, ${pill.label}`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setOpenDetailId(row.id);
                          }
                        }}
                      >
                        <td>
                          <span className="promo-mgmt-cell-code">
                            {row.code || "—"}
                          </span>
                        </td>
                        <td>{discountSummary(row.coupon)}</td>
                        <td
                          className="promo-mgmt-cell-num"
                          data-testid="row-usage"
                        >
                          {usageSummary(row)}
                        </td>
                        <td>
                          {formatPhoenixUnix(row.expiresAt) || "No expiry"}
                        </td>
                        <td>
                          <span
                            className={`promo-mgmt-pill promo-mgmt-pill--${pill.tone}`}
                            aria-label={`Status: ${pill.label}`}
                          >
                            {pill.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div
              className="promo-mgmt-footer-summary"
              aria-live="polite"
            >
              Showing {rows.length} promo code
              {rows.length === 1 ? "" : "s"} (Stripe-sourced)
            </div>
          </>
        )}
      </main>

      {openDetailId && (
        <PromoDetailDrawer
          promotionCodeId={openDetailId}
          onClose={() => setOpenDetailId(null)}
          onArchiveClick={(detail) => {
            setSuccessMsg(null);
            setArchiveError(null);
            setArchiveTarget({ id: detail.id, code: detail.code });
          }}
          archivedId={archivedId}
        />
      )}

      {/* RF-F — create form modal (AC Sc.1/2/4/5). */}
      <PromoCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />

      {/* RF-F — archive confirmation prompt (AC Sc.3). Stripe
          active:false deactivate, NOT a destructive delete. */}
      <PromoArchiveConfirm
        open={Boolean(archiveTarget)}
        code={archiveTarget?.code}
        submitting={archiving}
        errorMessage={archiveError}
        onConfirm={handleArchiveConfirm}
        onCancel={() => {
          if (archiving) return;
          setArchiveTarget(null);
          setArchiveError(null);
        }}
      />
    </div>
  );
};

export default StaffPromoManagementPage;
